import {
  JsonapiEntity,
  attribute,
  link,
  meta,
  relationship,
} from 'jsonapi-transformers';

import { formatDateForDisplay } from '®shared/dates/date-utils';

import { BibliographicResourceType } from './bibliographic-resource-types';
import { OnlineResource as OnlineResourceAliased } from './online-resource';

export enum ContributorType {
  PERSON = 'person',
  ORGANIZATION = 'organization',
}
export type Contributor = {
  first_name?: string;
  surname?: string;
  full_name?: string;
  type?: ContributorType;
};

type OnlineResource = OnlineResourceAliased;

type OnlineResourceSource = 'doi' | 'open_url' | 'uri';
type OnlineResourceSourceAndLink = {
  source: OnlineResourceSource;
  link: string | null;
};

export abstract class AbstractBibliographicResource<
  E extends AbstractBibliographicResource<E>,
> extends JsonapiEntity<E> {
  @attribute() authors?: Contributor[];
  @attribute() dates?: string[];
  @attribute() editors?: Contributor[];
  @attribute() edition?: string | null;
  @attribute() format?: string | null;
  @attribute() volume?: string | null;
  @attribute() issue?: string | null;
  @attribute({ name: 'isbn10s' })
  optionalIsbn10s?: string[];
  @attribute({ name: 'isbn13s' })
  optionalIsbn13s?: string[];
  @attribute() doi?: string | null;
  @attribute() lcn?: string | null;
  @attribute() publisher_names?: string[];
  @attribute() places_of_publication?: string[];
  @attribute() resource_type?: BibliographicResourceType | null;
  @attribute() title?: string;
  @attribute() issns?: string[];
  @attribute() eissns?: string[];
  @attribute() online_resource?: OnlineResourceSourceAndLink;
  @attribute() page_start?: string | null;
  @attribute() page_end?: string | null;
  @attribute() web_addresses?: string[];
  @attribute() source_uri?: string | null;
  @attribute() location?: string | null;
  @attribute() court?: string | null;
  @attribute() series?: string | null;
  @attribute() number?: string | null;
  @attribute() identifier?: string | null;

  @link() related?: string;

  @meta({ name: 'default_online_resource' })
  defaultOnlineResource?: OnlineResource;
  @meta({ name: 'all_online_resources' })
  allOnlineResources?: OnlineResource[];

  @relationship() part_of?: E;

  get authorNames(): string[] {
    return (this.authors || [])
      .map(getContributorName)
      .filter(name => (name ? true : false));
  }

  get editorNames(): string[] {
    return (this.editors || [])
      .map(getContributorName)
      .filter(name => (name ? true : false));
  }

  get pages(): string | null {
    if (this.page_end && this.page_start) {
      return `${this.page_start}-${this.page_end}`;
    }
    return null;
  }

  get partOfIsbns(): string[] {
    return this.part_of ? this.part_of.ownIsbns : [];
  }

  get isbn10s(): string[] {
    return this.optionalIsbn10s ? this.optionalIsbn10s : [];
  }

  set isbn10s(isbn10s: string[]) {
    this.optionalIsbn10s = isbn10s ? isbn10s : [];
  }

  get isbn13s(): string[] {
    return this.optionalIsbn13s ? this.optionalIsbn13s : [];
  }

  set isbn13s(isbn13s: string[]) {
    this.optionalIsbn13s = isbn13s ? isbn13s : [];
  }

  get isbns(): string[] {
    return Array.from(new Set([...this.ownIsbns, ...this.partOfIsbns]));
  }

  get ownIsbns(): string[] {
    return Array.from(new Set([...this.isbn10s, ...this.isbn13s]));
  }

  get dois(): string[] {
    const dois = this.doi ? [this.doi] : [];
    const partOfDois = this.part_of ? this.part_of.dois : [];
    return Array.from(new Set([...dois, ...partOfDois]));
  }

  get formattedDates(): string[] {
    return (this.dates || []).map(formatDateForDisplay);
  }

  get fullTitle(): string {
    if (!this.part_of?.title) {
      return this.title;
    }
    return $localize`:@@abstractBibliographicResource-value-fullTitle:${this.title} in ${this.part_of.title}`;
  }

  isBookOrChapterResource(): boolean {
    return (
      this.resource_type === BibliographicResourceType.Book ||
      this.resource_type === BibliographicResourceType.Chapter
    );
  }

  isArticleOrJournalResource(): boolean {
    return (
      this.resource_type === BibliographicResourceType.Article ||
      this.resource_type === BibliographicResourceType.Journal
    );
  }

  isPageResource(): boolean {
    return this.resource_type === BibliographicResourceType.Page;
  }

  isNonPageResource(): boolean {
    return this.resource_type !== BibliographicResourceType.Page;
  }

  isMusicScoreResource(): boolean {
    return this.resource_type === BibliographicResourceType.Score;
  }
}

function getContributorName(contributor: Contributor): string | null {
  const { first_name, surname, full_name } = contributor;
  if (full_name) {
    return full_name;
  } else if (first_name && surname) {
    return [first_name, surname].join(' ');
  } else if (first_name) {
    return first_name;
  } else if (surname) {
    return surname;
  }
  return null;
}
